import { AxiosResponse } from 'axios';
import {
  IGetAdminGuardiansParams,
  IGetAdminGuardiansResponse,
  IGetAdminGuardianChildrenResponse,
  IUnlinkGuardianChildResponse,
  ICreateAdminGuardianPayload,
  ICreateAdminGuardianResponse,
  IGetAdminGuardianByIdResponse,
  IGetAdminGuardianInstitutesResponse,
  IDeactivateGuardianPayload,
  IDeactivateGuardianResponse,
  IUnlinkGuardianFromInstitutePayload,
  IUnlinkGuardianFromInstituteResponse,
} from '../../../interface/classveew-admin/admin-guardian.interface';
import {
  IGuardianSearchResult,
  IManualGuardianCreationPayload,
} from '../../../interface/guardian.interface';
import api, { adminInstance } from '../../api';

// ===== Admin Guardian Management =====

/**
 * Get all guardians with pagination and filters
 */
export const getAdminGuardians = async (
  params: IGetAdminGuardiansParams,
): Promise<AxiosResponse<IGetAdminGuardiansResponse>> => {
  const response = await adminInstance.get<IGetAdminGuardiansResponse>('/guardians/global-list', {
    params,
  });
  return response;
};

/**
 * Get guardian by ID
 */
export const getAdminGuardianById = async (
  guardianId: string,
): Promise<AxiosResponse<IGetAdminGuardianByIdResponse>> => {
  const response = await adminInstance.get<IGetAdminGuardianByIdResponse>(
    `/guardians/${guardianId}`,
  );
  return response;
};

/**
 * Get institutes linked to a guardian
 */
export const getAdminGuardianInstitutes = async (
  guardianId: string,
): Promise<AxiosResponse<IGetAdminGuardianInstitutesResponse>> => {
  const response = await adminInstance.get<IGetAdminGuardianInstitutesResponse>(
    `/guardians/${guardianId}/organizations`,
  );
  return response;
};

/**
 * Update guardian profile
 */
export interface IUpdateAdminGuardianRequest {
  first_name?: string;
  last_name?: string;
  status?: boolean;
}

export const updateAdminGuardian = async (
  guardianId: string,
  payload: IUpdateAdminGuardianRequest,
): Promise<AxiosResponse<IDeactivateGuardianResponse>> => {
  const response = await adminInstance.patch<IDeactivateGuardianResponse>(
    `/guardians/${guardianId}`,
    payload,
  );
  return response;
};

/**
 * Deactivate a guardian globally
 */
export const deactivateAdminGuardian = async (
  guardianId: string,
  payload: IDeactivateGuardianPayload,
): Promise<AxiosResponse<IDeactivateGuardianResponse>> => {
  const response = await adminInstance.patch<IDeactivateGuardianResponse>(
    `/guardians/${guardianId}/deactivate`,
    payload,
  );
  return response;
};

/**
 * Reactivate a guardian
 */
export const reactivateAdminGuardian = async (
  guardianId: string,
): Promise<AxiosResponse<IDeactivateGuardianResponse>> => {
  const response = await adminInstance.patch<IDeactivateGuardianResponse>(
    `/guardians/${guardianId}/reactivate`,
  );
  return response;
};

/**
 * Delete a guardian permanently
 */
export const deleteAdminGuardian = async (
  guardianId: string,
): Promise<AxiosResponse<{ status: number; message: string }>> => {
  const response = await adminInstance.delete<{ status: number; message: string }>(
    `/guardians/${guardianId}`,
  );
  return response;
};

/**
 * Get children hierarchy linked to a guardian
 */
export const getAdminGuardianChildren = async (
  guardianId: string,
): Promise<AxiosResponse<IGetAdminGuardianChildrenResponse>> => {
  const response = await adminInstance.get<IGetAdminGuardianChildrenResponse>(
    `/guardians/${guardianId}/children`,
  );
  return response;
};

/**
 * Unlink a child from a guardian
 */
export const unlinkAdminGuardianChild = async (
  guardianId: string,
  studentId: string,
): Promise<AxiosResponse<IUnlinkGuardianChildResponse>> => {
  const response = await adminInstance.delete<IUnlinkGuardianChildResponse>(
    `/guardians/${guardianId}/children/${studentId}`,
  );
  return response;
};

/**
 * Unlink a guardian from a specific institute
 */
export const unlinkGuardianFromInstitute = async (
  guardianId: string,
  organizationId: string,
  payload: IUnlinkGuardianFromInstitutePayload,
): Promise<AxiosResponse<IUnlinkGuardianFromInstituteResponse>> => {
  const response = await adminInstance.delete<IUnlinkGuardianFromInstituteResponse>(
    `/guardians/${guardianId}/organizations/${organizationId}`,
    {
      data: payload,
    },
  );
  return response;
};

/**
 * Create a guardian via admin
 */
export const createAdminGuardian = async (
  payload: ICreateAdminGuardianPayload,
): Promise<AxiosResponse<ICreateAdminGuardianResponse>> => {
  const response = await adminInstance.post<ICreateAdminGuardianResponse>('/guardians', payload);
  return response;
};

// ===== Guardian Onboarding (Search, Link, Send Invite, Manual Create) =====

/** Unwraps standard Classveew API envelope: { status, message, data } */
const unwrapApiData = <T>(body: unknown): T => {
  if (body == null) return body as T;
  const envelope = body as Record<string, unknown>;
  return (envelope.data ?? envelope.responseArray ?? body) as T;
};

/**
 * Search for a guardian by email (school-level admin endpoint)
 */
export const searchGuardianByEmail = async (
  email: string,
  studentId?: string,
): Promise<IGuardianSearchResult> => {
  const params: Record<string, string> = { email };
  if (studentId) params.studentId = studentId;
  const response = await api.get<IGuardianSearchResult>('/guardian/search-guardian', { params });
  return unwrapApiData<IGuardianSearchResult>(response.data);
};

/**
 * Link an existing guardian to a student
 */
export const adminLinkGuardian = async (payload: {
  guardianId: string;
  studentId: string;
  relationshipId: string;
}): Promise<Record<string, unknown>> => {
  const response = await api.post('/guardian/global-link', payload);
  return unwrapApiData<Record<string, unknown>>(response.data);
};

/**
 * Send an invite to a guardian email
 */
export const sendGuardianInvite = async (payload: {
  email: string;
  studentId: string;
}): Promise<Record<string, unknown>> => {
  const response = await api.post('/guardian/global-invite', payload);
  return unwrapApiData<Record<string, unknown>>(response.data);
};

/**
 * Manually create a new guardian and link to student
 */
export const manualCreateGuardian = async (
  payload: IManualGuardianCreationPayload,
): Promise<Record<string, unknown>> => {
  const response = await api.post('/guardian/global-create', payload);
  return unwrapApiData<Record<string, unknown>>(response.data);
};
